import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';
import { useMarkAsSold, useSplitItem, useRecordTrade, TradeItemInput } from '@/hooks/useTransactions';
import { useUploadItemImages, useDuplicateUserItem, useUpdateUserItem, useReorderItemImages, useDeleteUserItemImage } from '@/hooks/useUserItems';
import { useUIStore } from '@/store/uiStore';
import { UserItem } from '@/types/userItem';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { DeleteItemsConfirmDialog } from '@/components/inventory/DeleteItemsConfirmDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PRICE_INPUT_PROPS } from '@/lib/priceInputProps';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useIsMobile } from '@/hooks/use-mobile';
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset';
import { Lock, LockOpen, Pencil, Tag, MessageCircle, CheckCircle, ChevronDown, ChevronLeft, Camera, Loader2, Images, Upload, Scissors, AlertTriangle, X, CalendarIcon, Plus, Search, ArrowRightLeft, DollarSign, Trash2, MoreHorizontal, ShoppingBag, SlidersHorizontal, Settings2, Copy, Shield, Check, ArrowUp, ArrowDown, ArrowUpDown, ExternalLink } from 'lucide-react';
import { PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { ItemFormModal } from '@/components/item/ItemFormModal';
import { resolveCollectrPriceCents, formatCents } from '@/lib/collectrPricing';
import { ActionMenu, type ActionMenuItem } from '@/components/ui/ActionMenu';
import { ImageSelectionDialog } from '@/components/item/ImageSelectionDialog';
import { InventorySearchPopover } from './InventorySearchPopover';
import { TradeItem, TradeItemCard } from '@/components/trade/TradeItemCard';
import { TradeSummary } from '@/components/trade/TradeSummary';

import { cn } from '@/lib/utils';
import { goldGradientActive } from '@/lib/gradientStyles';
import { hasOnlyStockPhoto, STOCK_PHOTO_BLOCK_TITLE, STOCK_PHOTO_BLOCK_DESC, TIMESTAMP_TIP } from '@/lib/listingEligibility';
import { patchItemInCaches, removeItemFromCaches, restoreItemCaches } from '@/lib/optimisticItems';

import { EbayListingDialog } from './EbayListingDialog';
import { useEbayConnection } from '@/hooks/useEbayConnection';
import { useEbayListing } from '@/hooks/useEbayListing';
import { EbayBag } from '@/components/icons/EbayBag';
import { PAYMENT_METHODS } from '@/components/quickPriceEdit/paymentMethods';
import { ManagePhotosDialog } from '@/components/quickPriceEdit/ManagePhotosDialog';
import { DeletePhotoConfirmDialog } from '@/components/quickPriceEdit/DeletePhotoConfirmDialog';
import { SwipeableSheetContent } from '@/components/quickPriceEdit/SwipeableSheetContent';

import { MobileFullscreenPage } from '@/components/layout/MobileFullscreenPage';
import { SaleReviewPrompt } from '@/components/quickPriceEdit/SaleReviewPrompt';
import { useReceivedInterests } from '@/hooks/useItemInterests';
import { formatUsd, formatUsdCents } from '@/lib/formatMoney';
import { STORAGE_BUCKETS } from '@/lib/storageBuckets';
interface QuickPriceEditDialogProps {
  item: UserItem;
  open: boolean;
  onClose: () => void;
  /** When provided, opens the Sold/Trade section directly in the chosen mode. */
  defaultMode?: 'sold' | 'traded';
  /** Pre-seed additional "trading away" inventory items (in addition to the seed `item`). */
  additionalGaveItems?: UserItem[];
}

// SwipeableSheetContent moved to ./quickPriceEdit/SwipeableSheetContent



// SortablePhotoRow moved to ./quickPriceEdit/SortablePhotoRow
// (used internally by ManagePhotosDialog — no direct call sites here anymore)

export function QuickPriceEditDialog({
  item,
  open,
  onClose,
  defaultMode,
  additionalGaveItems
}: QuickPriceEditDialogProps) {
  const [price, setPrice] = useState('');
  const [cost, setCost] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCost, setShowCost] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [titleEditOpen, setTitleEditOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState(item.title || '');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [ebayDialogOpen, setEbayDialogOpen] = useState(false);
  const { data: ebayConnection } = useEbayConnection();
  const { data: ebayListing, refresh: refreshEbayListing } = useEbayListing(item.id);

  // Management toggles
  const [isListed, setIsListed] = useState(false);
  const [acceptsOffers, setAcceptsOffers] = useState(false);
  const [showSoldSection, setShowSoldSection] = useState(false);

  // Sold/Trade mode
  const [transactionMode, setTransactionMode] = useState<'sold' | 'traded'>('sold');

  // Sale state (for sold mode)
  const [salePrice, setSalePrice] = useState('');
  const [soldQuantity, setSoldQuantity] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [saleDate, setSaleDate] = useState<Date>(new Date());
  const [salePhotoUrl, setSalePhotoUrl] = useState<string | null>(null);
  const [isUploadingSalePhoto, setIsUploadingSalePhoto] = useState(false);
  const [buyerSource, setBuyerSource] = useState<string>('other');
  const [pendingReview, setPendingReview] = useState<{
    transactionId: string;
    buyerId: string;
    buyerName: string;
  } | null>(null);

  const { data: receivedInterests } = useReceivedInterests();
  const acceptedOffers = (receivedInterests || []).filter(
    (i) => i.item_id === item.id && i.status === 'accepted',
  );

  // Trade state (for traded mode)
  const [receivedItems, setReceivedItems] = useState<TradeItem[]>([]);
  const [gaveItems, setGaveItems] = useState<TradeItem[]>([]);
  const [cashReceived, setCashReceived] = useState('');
  const [cashPaid, setCashPaid] = useState('');
  const [showReceivedSearch, setShowReceivedSearch] = useState(false);
  const [showGaveSearch, setShowGaveSearch] = useState(false);
  const [newReceivedTitle, setNewReceivedTitle] = useState('');
  const [newReceivedValue, setNewReceivedValue] = useState('');

  // Split confirmation state
  const [showSplitConfirm, setShowSplitConfirm] = useState(false);
  const [isSplitting, setIsSplitting] = useState(false);
  // Per-image target copy index (0 = original/Copy 1, 1..N-1 = new drafts). Keyed by image id.
  const [splitImageTargets, setSplitImageTargets] = useState<Record<string, number>>({});

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const isOnItemPage = location.pathname.startsWith('/item/');
  const markAsSold = useMarkAsSold();
  const recordTrade = useRecordTrade();
  const splitItem = useSplitItem();
  const uploadImages = useUploadItemImages();
  const updateItem = useUpdateUserItem();
  const duplicateItem = useDuplicateUserItem();
  const reorderImages = useReorderItemImages();
  const deleteImage = useDeleteUserItemImage();
  const [reorderOpen, setReorderOpen] = useState(false);
  const [reorderDraft, setReorderDraft] = useState<NonNullable<UserItem['images']>>([]);
  const [isSavingReorder, setIsSavingReorder] = useState(false);
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 6 } }),
  );
  const [pendingDeleteUrl, setPendingDeleteUrl] = useState<string | null>(null);
  const [isDeletingPhoto, setIsDeletingPhoto] = useState(false);
  const [verifyingProofUrl, setVerifyingProofUrl] = useState<string | null>(null);
  const [isSavingProof, setIsSavingProof] = useState(false);
  const [isVerifyingProof, setIsVerifyingProof] = useState(false);
  const [proofError, setProofError] = useState<{ message: string; missing?: string[]; notes?: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const proofCameraInputRef = useRef<HTMLInputElement>(null);
  const proofGalleryInputRef = useRef<HTMLInputElement>(null);
  const salePhotoInputRef = useRef<HTMLInputElement>(null);
  const tradePhotoInputRef = useRef<HTMLInputElement>(null);
  const [tradePhotoUrl, setTradePhotoUrl] = useState<string | null>(null);
  const [isUploadingTradePhoto, setIsUploadingTradePhoto] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const isMobile = useIsMobile();
  const { keyboardHeight } = useVisualViewportInset();

  // Pre-fill with current values when dialog opens
  useEffect(() => {
    if (open) {
      setPrice(item.asking_price_cents ? (item.asking_price_cents / 100).toFixed(2) : '');
      setCost(item.cost_basis_cents ? (item.cost_basis_cents / 100).toFixed(2) : '');
      setShowCost(false);
      setIsListed(item.visibility === 'listed');
      // Default unlisted items to "Open to Offers" ON — users almost always want it,
      // and it avoids the extra tap when flipping an unlisted item live.
      setAcceptsOffers(item.visibility === 'listed' ? item.is_negotiable : (item.is_negotiable ?? true));
      setShowSoldSection(!!defaultMode);
      setTransactionMode(defaultMode ?? 'sold');
      setSalePrice('');
      setSoldQuantity(1);
      setPaymentMethod('');
      setSaleDate(new Date());
      setSalePhotoUrl(null);
      setTradePhotoUrl(null);
      setShowSplitConfirm(false);
      // Reset trade state
      setReceivedItems([]);
      setGaveItems(
        (additionalGaveItems ?? []).map((it) => ({
          id: `seed-${it.id}`,
          title: it.title,
          estimatedValueCents: it.cost_basis_cents ?? null,
          isFromInventory: true,
          inventoryItemId: it.id,
        })),
      );
      setCashReceived('');
      setCashPaid('');
      setTitleDraft(item.title || '');
      setTitleEditOpen(false);
    }
  }, [open, item.asking_price_cents, item.cost_basis_cents, item.visibility, item.is_negotiable, item.title, defaultMode, additionalGaveItems]);
  const handleClose = () => {
    setPrice('');
    setShowSoldSection(false);
    setTransactionMode('sold');
    setSalePrice('');
    setSoldQuantity(1);
    setPaymentMethod('');
    setSalePhotoUrl(null);
    setTradePhotoUrl(null);
    setShowSplitConfirm(false);
    setBuyerSource('other');
    setPendingReview(null);
    // Reset trade state
    setReceivedItems([]);
    setGaveItems([]);
    setCashReceived('');
    setCashPaid('');
    onClose();
  };

  // Check if we need to show split confirmation when listing
  const needsSplitForListing = item.quantity > 1 && (item.category === 'singles' || item.category === 'slabs');
  const handleSaveTitle = async () => {
    const next = titleDraft.trim();
    if (next.length < 2) {
      toast.error('Title too short', { description: 'Title must be at least 2 characters.' });
      return;
    }
    if (next.length > 100) {
      toast.error('Title too long', { description: 'Keep it under 100 characters.' });
      return;
    }
    if (next === item.title) {
      setTitleEditOpen(false);
      return;
    }
    setIsSavingTitle(true);
    try {
      const { error } = await supabase.from('user_items').update({ title: next }).eq('id', item.id);
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ['my-items', item.user_id] });
      await queryClient.invalidateQueries({ queryKey: ['user-item', item.id] });
      await queryClient.invalidateQueries({ queryKey: ['marketplace-items'] });
      toast.success('Title updated');
      setTitleEditOpen(false);
    } catch (err) {
      console.error('Error updating title:', err);
      toast.error('Failed to update title');
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleSave = async () => {
    // If trying to list singles/slabs with qty > 1, show split confirmation
    if (isListed && needsSplitForListing && !item.is_listed) {
      setShowSplitConfirm(true);
      return;
    }
    await performSave();
  };
  const performSave = async () => {
    // Price can be null (empty) or 0+
    const priceValue = price.trim() ? parseFloat(price) : null;
    if (priceValue !== null && (isNaN(priceValue) || priceValue < 0)) {
      toast.error('Invalid price', {
        description: 'Please enter a valid price.'
      });
      return;
    }

    // Cost basis can be null (empty) or 0+
    const costValue = cost.trim() ? parseFloat(cost) : null;
    if (costValue !== null && (isNaN(costValue) || costValue < 0)) {
      toast.error('Invalid cost', {
        description: 'Please enter a valid cost basis.'
      });
      return;
    }

    // If listing with no price, must accept offers
    if (isListed && !priceValue && !acceptsOffers) {
      toast.error('Price or offers required', {
        description: 'Set a price or enable "Open to Offers" to list this item.'
      });
      return;
    }
    // Block listing for items that only have a stock photo
    if (isListed && hasOnlyStockPhoto(item)) {
      toast.error(STOCK_PHOTO_BLOCK_TITLE, { description: STOCK_PHOTO_BLOCK_DESC });
      return;
    }
    setIsSubmitting(true);
    const optimisticPatch = {
      asking_price_cents: priceValue !== null ? Math.round(priceValue * 100) : null,
      cost_basis_cents: costValue !== null ? Math.round(costValue * 100) : null,
      visibility: (isListed ? 'listed' : 'private') as 'listed' | 'private',
      is_negotiable: acceptsOffers,
      is_listed: isListed,
    };
    // Optimistic: the drawer closes on success, so the grid behind it must
    // already show the new price / listed state.
    const snapshot = patchItemInCaches(queryClient, item.id, optimisticPatch);
    try {
      const {
        error
      } = await supabase.from('user_items').update(optimisticPatch).eq('id', item.id);
      if (error) throw error;
      await queryClient.invalidateQueries({
        queryKey: ['my-items', item.user_id]
      });
      await queryClient.invalidateQueries({ queryKey: ['user-item', item.id] });
      await queryClient.invalidateQueries({ queryKey: ['marketplace-items'] });


      // Auto-push a price change to an active fixed-price eBay listing so
      // repricing after the fact doesn't need a second manual "Refresh".
      const newCents = priceValue !== null ? Math.round(priceValue * 100) : null;
      const ebayNeedsPush =
        !!ebayListing &&
        ebayListing.status === 'active' &&
        ebayListing.format !== 'auction' &&
        newCents !== null &&
        newCents > 0 &&
        newCents !== (item.asking_price_cents ?? null);
      if (ebayNeedsPush) {
        refreshEbayListing.mutate();
      }

      toast.success('Item updated', {
        description: isListed ? `Listed${priceValue ? ` at ${formatUsd(priceValue)}` : ''}` : 'Changes saved'
      });
      handleClose();

    } catch (error) {
      restoreItemCaches(queryClient, snapshot);
      console.error('Error updating item:', error);

      toast.error('Error', {
        description: 'Failed to update item. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleSplitAndList = async () => {
    setIsSplitting(true);
    try {
      const result = await splitItem.mutateAsync({
        itemId: item.id,
        imageAssignments: splitImageTargets,
      });
      toast.success(`Split into ${result.totalItems} items`);
      setShowSplitConfirm(false);
      setSplitImageTargets({});
      handleClose();
    } catch (error) {
      console.error('Error splitting item:', error);
      toast.error('Failed to split items');
    } finally {
      setIsSplitting(false);
    }
  };
  const handleMarkAsSold = async () => {
    setIsSubmitting(true);
    try {
      // Resolve buyer from accepted-offer picker (falls back to external / "other")
      let buyerId: string | undefined;
      let buyerName: string | undefined;
      let salePriceCents = salePrice.trim() ? Math.round(parseFloat(salePrice) * 100) : undefined;
      if (buyerSource !== 'other') {
        const offer = acceptedOffers.find((o) => o.id === buyerSource);
        if (offer) {
          buyerId = offer.sender_id;
          buyerName = offer.sender?.username || 'CardSimp User';
          if (salePriceCents === undefined && offer.offer_cents) {
            salePriceCents = offer.offer_cents;
          }
        }
      }

      const result = await markAsSold.mutateAsync({
        itemId: item.id,
        buyerId,
        salePriceCents,
        quantitySold: soldQuantity,
        paymentMethod: paymentMethod || undefined,
        salePhotoUrl: salePhotoUrl || undefined,
        saleDate
      });
      const quantityText = soldQuantity > 1 ? ` (${soldQuantity} units)` : '';
      const remainingText = result.remainingQuantity > 0 ? ` ${result.remainingQuantity} remaining in inventory.` : '';
      toast.success('Item marked as sold!', {
        description: salePriceCents ? `Sold for ${formatUsdCents(salePriceCents)}${quantityText}.${remainingText}` : `Sale recorded${quantityText}.${remainingText}`
      });

      // If sale is linked to a known buyer, defer close until review prompt is dismissed
      if (buyerId && result?.transaction) {
        setPendingReview({
          transactionId: result.transaction.id,
          buyerId,
          buyerName: buyerName || 'the buyer',
        });
      } else {
        handleClose();
      }
    } catch (error) {
      console.error('Error marking as sold:', error);
      toast.error('Failed to mark as sold');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleRecordTrade = async () => {
    setIsSubmitting(true);
    try {
      const cashReceivedCents = cashReceived.trim() ? Math.round(parseFloat(cashReceived) * 100) : undefined;
      const cashPaidCents = cashPaid.trim() ? Math.round(parseFloat(cashPaid) * 100) : undefined;

      // Build trade items array
      const tradeItems: TradeItemInput[] = [];

      // Add received items with inventory creation data
      receivedItems.forEach(ri => {
        tradeItems.push({
          direction: 'received',
          itemId: ri.isFromInventory ? ri.inventoryItemId : undefined,
          externalTitle: ri.isFromInventory ? undefined : ri.title,
          estimatedValueCents: ri.estimatedValueCents || undefined,
          // Pass inventory creation fields for external items
          addToInventory: !ri.isFromInventory ? ri.addToInventory : undefined,
          category: !ri.isFromInventory ? ri.category : undefined,
          condition: !ri.isFromInventory ? ri.condition : undefined,
          game: !ri.isFromInventory ? ri.game : undefined
        });
      });

      // Add gave items (additional ones from inventory)
      gaveItems.forEach(gi => {
        tradeItems.push({
          direction: 'gave',
          itemId: gi.inventoryItemId,
          estimatedValueCents: gi.estimatedValueCents || undefined
        });
      });
      const result = await recordTrade.mutateAsync({
        itemId: item.id,
        tradeItems,
        cashReceivedCents,
        cashPaidCents,
        saleDate,
        tradePhotoUrl: tradePhotoUrl || undefined
      });

      // Build description with inventory addition info
      let description = `Successfully traded ${item.title}`;
      if (result.itemsAddedToInventory > 0) {
        description += `. ${result.itemsAddedToInventory} item${result.itemsAddedToInventory > 1 ? 's' : ''} added to inventory.`;
      }
      toast.success('Trade recorded!', {
        description
      });
      handleClose();
    } catch (error) {
      console.error('Error recording trade:', error);
      toast.error('Failed to record trade');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to add inventory item to received
  const handleAddReceivedFromInventory = (inventoryItem: UserItem) => {
    const imageUrl = inventoryItem.images?.[0]?.image_url || inventoryItem.stock_image_url;
    setReceivedItems(prev => [...prev, {
      id: `received-${inventoryItem.id}`,
      title: inventoryItem.title,
      imageUrl,
      estimatedValueCents: inventoryItem.cost_basis_cents,
      isFromInventory: true,
      inventoryItemId: inventoryItem.id
    }]);
  };

  // Helper to add external item to received
  const handleAddExternalReceived = (tradeItem: TradeItem) => {
    setReceivedItems(prev => [...prev, tradeItem]);
  };

  // Quick placeholder: add an unspecified incoming item the user will detail later from Inventory.
  // Useful at-show to capture the trade fast; fills as a private draft in inventory.
  const handleAddPlaceholderReceived = () => {
    const nextNum = receivedItems.filter(r => r.title.startsWith('Trade-in (needs details)')).length + 1;
    const placeholder: TradeItem = {
      id: `placeholder-${Date.now()}-${nextNum}`,
      title: `Trade-in (needs details) #${nextNum}`,
      imageUrl: null,
      estimatedValueCents: null,
      isFromInventory: false,
      addToInventory: true,
      category: 'singles',
      condition: 'near_mint',
      game: item.game || 'pokemon',
    };
    setReceivedItems(prev => [...prev, placeholder]);
  };

  // Inline composer handler — blank title falls back to the placeholder path.
  const handleAddReceivedInline = () => {
    const title = newReceivedTitle.trim();
    if (!title) {
      handleAddPlaceholderReceived();
    } else {
      const valueNum = newReceivedValue.trim() ? parseFloat(newReceivedValue) : NaN;
      const valueCents = !isNaN(valueNum) ? Math.round(valueNum * 100) : null;
      handleAddExternalReceived({
        id: `external-${Date.now()}`,
        title,
        imageUrl: null,
        estimatedValueCents: valueCents,
        isFromInventory: false,
        addToInventory: true,
        category: 'singles',
        condition: 'near_mint',
        game: item.game || 'pokemon',
      });
    }
    setNewReceivedTitle('');
    setNewReceivedValue('');
  };

  // Helper to add inventory item to gave
  const handleAddGaveFromInventory = (inventoryItem: UserItem) => {
    const imageUrl = inventoryItem.images?.[0]?.image_url || inventoryItem.stock_image_url;
    setGaveItems(prev => [...prev, {
      id: `gave-${inventoryItem.id}`,
      title: inventoryItem.title,
      imageUrl,
      estimatedValueCents: inventoryItem.cost_basis_cents,
      isFromInventory: true,
      inventoryItemId: inventoryItem.id
    }]);
  };

  const handleAddManyGaveFromInventory = (inventoryItems: UserItem[]) => {
    setGaveItems(prev => {
      const existing = new Set(prev.map(g => g.inventoryItemId).filter(Boolean));
      const additions = inventoryItems
        .filter(i => !existing.has(i.id))
        .map(i => ({
          id: `gave-${i.id}`,
          title: i.title,
          imageUrl: i.images?.[0]?.image_url || i.stock_image_url,
          estimatedValueCents: i.cost_basis_cents,
          isFromInventory: true,
          inventoryItemId: i.id,
        }));
      return [...prev, ...additions];
    });
  };

  // Get excluded IDs for inventory search (items already added)
  const getExcludedIds = () => {
    const ids = [item.id]; // Always exclude the current item
    gaveItems.forEach(g => g.inventoryItemId && ids.push(g.inventoryItemId));
    return ids;
  };
  const handleSalePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { processSelectedFiles, resetFileInput } = await import('@/lib/photoInputUtils');
    const [file] = await processSelectedFiles(event.target.files, { multiple: false });
    resetFileInput(event.target);
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }
    setIsUploadingSalePhoto(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { resizeImage, IMMUTABLE_CACHE_CONTROL } = await import('@/lib/imageResizeUtils');
      const compressed = await resizeImage(file);
      const fileExt = (compressed.name.split('.').pop() || 'jpg').toLowerCase();
      const fileName = `${user.id}/sale-${item.id}-${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from(STORAGE_BUCKETS.listingImages)
        .upload(fileName, compressed, { contentType: compressed.type, cacheControl: IMMUTABLE_CACHE_CONTROL });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from(STORAGE_BUCKETS.listingImages).getPublicUrl(fileName);
      setSalePhotoUrl(publicUrl);
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setIsUploadingSalePhoto(false);
    }
  };
  const handleTradePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { processSelectedFiles, resetFileInput } = await import('@/lib/photoInputUtils');
    const [file] = await processSelectedFiles(event.target.files, { multiple: false });
    resetFileInput(event.target);
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }
    setIsUploadingTradePhoto(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { resizeImage, IMMUTABLE_CACHE_CONTROL } = await import('@/lib/imageResizeUtils');
      const compressed = await resizeImage(file);
      const fileExt = (compressed.name.split('.').pop() || 'jpg').toLowerCase();
      const fileName = `${user.id}/trade-${item.id}-${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from(STORAGE_BUCKETS.listingImages)
        .upload(fileName, compressed, { contentType: compressed.type, cacheControl: IMMUTABLE_CACHE_CONTROL });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from(STORAGE_BUCKETS.listingImages).getPublicUrl(fileName);
      setTradePhotoUrl(publicUrl);
    } catch (error) {
      console.error('Error uploading trade photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setIsUploadingTradePhoto(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSubmitting) {
      handleSave();
    }
  };
  const handleDeleteItem = async () => {
    setIsDeleting(true);
    // Optimistic: drop the row from the grid immediately — the eBay
    // end-listing call below can take seconds.
    const deleteSnapshot = removeItemFromCaches(queryClient, item.id);
    try {
      const { endEbayListingsForItems } = await import('@/lib/endEbayListingsForItems');
      await endEbayListingsForItems([item.id]);
      const {
        error
      } = await supabase.from('user_items').delete().eq('id', item.id);
      if (error) throw error;
      await queryClient.invalidateQueries({
        queryKey: ['my-items', item.user_id]
      });
      await queryClient.invalidateQueries({ queryKey: ['user-item', item.id] });
      await queryClient.invalidateQueries({ queryKey: ['marketplace-items'] });
      toast.success('Item deleted');
      setShowDeleteConfirm(false);
      handleClose();
    } catch (error) {
      restoreItemCaches(queryClient, deleteSnapshot);
      console.error('Failed to delete item:', error);
      toast.error('Failed to delete item');

    } finally {
      setIsDeleting(false);
    }
  };
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { processSelectedFiles, resetFileInput } = await import('@/lib/photoInputUtils');
    const files = await processSelectedFiles(e.target.files, { maxCount: 10 });
    // Always clear the input so re-picking the same file fires onChange again
    resetFileInput(e.target);
    if (files.length === 0) return;
    setIsUploading(true);
    try {
      await uploadImages.mutateAsync({
        itemId: item.id,
        files,
      });
      await queryClient.invalidateQueries({
        queryKey: ['my-items']
      });
      await queryClient.invalidateQueries({
        queryKey: ['user-item', item.id]
      });
      toast.success(files.length > 1 ? `${files.length} photos added` : 'Photo added');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const existingProof = (item.proof_images as Record<string, string> | null) || {};
  const hasPossession = Boolean(existingProof.possession);
  const proofEligible = item.category === 'singles' || item.category === 'slabs' || item.category === 'sealed';

  const handleSetProofUrl = async (url: string | null) => {
    setIsSavingProof(true);
    try {
      const next: Record<string, string> = { ...existingProof };
      if (url) {
        next.possession = url;
      } else {
        delete next.possession;
      }
      const merged = Object.keys(next).length ? next : null;
      await updateItem.mutateAsync({
        id: item.id,
        proof_images: merged,
        has_proof_pack: merged ? Object.keys(merged).length >= 3 : false,
      });
      await queryClient.invalidateQueries({ queryKey: ['my-items'] });
      await queryClient.invalidateQueries({ queryKey: ['user-item', item.id] });
      toast.success(url ? 'Proof photo set' : 'Proof photo cleared');
      setProofError(null);
    } catch (error) {
      console.error('Error setting proof:', error);
      toast.error('Could not update proof');
    } finally {
      setIsSavingProof(false);
    }
  };

  const verifyAndSetProof = async (url: string) => {
    setProofError(null);
    setIsVerifyingProof(true);
    setVerifyingProofUrl(url);
    try {
      const { data, error } = await supabase.functions.invoke('verify-proof-photo', {
        body: { image_url: url },
      });
      if (error) {
        toast.error(error.message || 'Verification failed, try again');
        return;
      }
      if (data?.verified) {
        await handleSetProofUrl(url);
        toast.success('Verified ✓');
      } else {
        const missing: string[] = data?.missing || [];
        const labels: Record<string, string> = {
          handle: 'Your @handle on paper',
          date: "A recent date on paper (last 7 days)",
          paper: 'Handwritten on a physical piece of paper',
          item: 'A trading-card item visible in frame',
        };
        const list = missing.length ? missing.map(m => labels[m] || m) : ['Could not verify'];
        const notes: string | undefined = typeof data?.notes === 'string' && data.notes ? data.notes : undefined;
        setProofError({ message: "Couldn't verify this photo. Missing:", missing: list, notes });
      }
    } catch (e: any) {
      console.error('verify-proof-photo error', e);
      toast.error('Verification failed, try again');
    } finally {
      setIsVerifyingProof(false);
      setVerifyingProofUrl(null);
    }
  };

  const handleProofPhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { processSelectedFiles, resetFileInput } = await import('@/lib/photoInputUtils');
    const [file] = await processSelectedFiles(event.target.files, { multiple: false });
    resetFileInput(event.target);
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }
    setProofError(null);
    setIsVerifyingProof(true);
    try {
      // Upload as a regular item photo first so it lives in the gallery
      await uploadImages.mutateAsync({ itemId: item.id, files: [file] });
      await queryClient.invalidateQueries({ queryKey: ['my-items'] });
      await queryClient.invalidateQueries({ queryKey: ['user-item', item.id] });

      // Find the newly uploaded image URL by re-fetching the item
      const { data: imgs } = await supabase
        .from('user_item_images')
        .select('image_url, display_order')
        .eq('item_id', item.id)
        .order('display_order', { ascending: false })
        .limit(1);
      const newest = imgs?.[0];
      if (!newest?.image_url) {
        toast.error('Upload succeeded but could not find photo');
        return;
      }
      await verifyAndSetProof(newest.image_url);
    } catch (e) {
      console.error('Proof upload error', e);
      toast.error('Upload failed');
    } finally {
      setIsVerifyingProof(false);
    }
  };
  const sortedImages = [...(item.images || [])].sort((a, b) => a.display_order - b.display_order);
  const photoCount = sortedImages.length;
  const hasImage = sortedImages[0]?.image_url || item.stock_image_url;
  const displayImage = sortedImages[0]?.image_url || item.stock_image_url;
  const isStockImage = !sortedImages[0]?.image_url && !!item.stock_image_url;
  const metaParts: string[] = [];
  if (photoCount >= 2) metaParts.push(`${photoCount} photos`);
  else if (isStockImage) metaParts.push('Sample photo only');
  if (hasPossession) metaParts.push('✓ Verified');
  const metaLine = metaParts.join(' · ');

  const openReorder = () => {
    setReorderDraft(sortedImages);
    setProofError(null);
    setReorderOpen(true);
  };
  const saveReorder = async () => {
    setIsSavingReorder(true);
    try {
      const orderedImageIds = reorderDraft.map(img => img.id);
      await reorderImages.mutateAsync({ itemId: item.id, orderedImageIds });
      toast.success('Photo order saved');
      setReorderOpen(false);
    } catch (e) {
      console.error(e);
      toast.error('Could not save order');
    } finally {
      setIsSavingReorder(false);
    }
  };

  const confirmDeletePhoto = async () => {
    if (!pendingDeleteUrl) return;
    const target = sortedImages.find(img => img.image_url === pendingDeleteUrl);
    if (!target?.id) {
      setPendingDeleteUrl(null);
      return;
    }
    setIsDeletingPhoto(true);
    try {
      await deleteImage.mutateAsync(target.id);
      setReorderDraft(prev => prev.filter(img => img.image_url !== pendingDeleteUrl));
      toast.success('Photo deleted');
      setPendingDeleteUrl(null);
    } catch (e) {
      console.error(e);
      toast.error('Could not delete photo');
    } finally {
      setIsDeletingPhoto(false);
    }
  };

  const salePriceNum = salePrice.trim() ? parseFloat(salePrice) : null;
  const costNum = item.cost_basis_cents ? item.cost_basis_cents / 100 : null;
  const profitLoss = salePriceNum !== null && costNum !== null ? salePriceNum - costNum : null;
  const stockBlocked = hasOnlyStockPhoto(item);
  const canList = !stockBlocked;
  const statusSummary = (() => {
    const isCurrentlyListed = item.visibility === 'listed';
    if (isCurrentlyListed && item.asking_price_cents) return `Listed at ${formatUsdCents(item.asking_price_cents)}`;
    if (isCurrentlyListed && item.is_negotiable) return 'Listed — taking offers';
    if (item.is_negotiable) return 'Taking offers';
    if (item.asking_price_cents) return `Priced at ${formatUsdCents(item.asking_price_cents)} — unlisted`;
    return 'Unlisted';
  })();

  // Photo menu — bottom sheet on mobile, dropdown on desktop
  const photoTrigger = hasImage ? (
    <button type="button" className="relative cursor-pointer group/photo shrink-0">
      <div className="relative w-20 h-20">
        <img src={displayImage} alt={item.title} className="w-20 h-20 rounded-md object-cover" />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/photo:opacity-100 flex items-center justify-center rounded-md transition-opacity">
          {isUploading ? <Loader2 className="w-6 h-6 text-white animate-spin" /> : <Camera className="w-6 h-6 text-white" />}
        </div>
        {hasPossession ? (
          <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-active-gold border-2 border-chrome flex items-center justify-center shadow-md" aria-label="Possession verified">
            <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />
          </div>
        ) : isMobile ? (
          <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary border-2 border-chrome flex items-center justify-center shadow-md">
            <Camera className="w-3 h-3 text-primary-foreground" />
          </div>
        ) : null}
        {photoCount >= 2 && (
          <span className="absolute top-0.5 left-0.5 text-[10px] font-medium bg-black/70 text-white px-1.5 py-0.5 rounded">
            1/{photoCount}
          </span>
        )}
        {isStockImage && photoCount < 2 && (
          <span className="absolute top-0.5 left-0.5 text-[9px] bg-black/60 px-1 rounded">Sample Photo</span>
        )}
      </div>
    </button>
  ) : (
    <button type="button" className="w-20 h-20 rounded-md bg-white/[0.05] border border-dashed border-white/15 flex flex-col items-center justify-center gap-1 hover:bg-white/[0.08] transition-colors shrink-0">
      {isUploading ? <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" /> : <>
        <Camera className="w-6 h-6 text-muted-foreground" />
        <span className="text-[11px] text-muted-foreground font-medium">Add photo</span>
      </>}
    </button>
  );

  const photoMenuItems: ActionMenuItem[] = [
    ...(photoCount >= 1 ? [{
      key: 'manage',
      label: (
        <span className="flex-1 flex items-center justify-between gap-2">
          <span>Manage Photos…</span>
          {hasPossession && <Shield className="w-4 h-4 text-active-gold fill-active-gold/15" strokeWidth={2} />}
        </span>
      ),
      icon: <ArrowUpDown className="w-5 h-5" />,
      onSelect: openReorder,
    } as ActionMenuItem] : []),
    ...(isMobile ? [{
      key: 'camera',
      label: 'Take Photo',
      icon: <Camera className="w-5 h-5" />,
      onSelect: () => cameraInputRef.current?.click(),
      separatorBefore: photoCount >= 1,
    } as ActionMenuItem] : []),
    {
      key: 'upload',
      label: 'Add Photos',
      icon: <Upload className="w-5 h-5" />,
      onSelect: () => fileInputRef.current?.click(),
      separatorBefore: photoCount >= 1 && !isMobile,
    },
    {
      key: 'stock',
      label: 'Choose Sample Photo',
      icon: <Images className="w-5 h-5" />,
      onSelect: () => setImageDialogOpen(true),
    },
  ];

  const PhotoControl = (
    <ActionMenu trigger={photoTrigger} items={photoMenuItems} align="start" mobileTitle="Photos" />
  );

  // Header (item preview + stock-photo warning) — shared by both layouts
  const HeaderBlock = (
    <div className={cn(isMobile ? "space-y-2" : "space-y-3")}>
      <div className={cn("flex items-center gap-3 rounded-lg bg-black/20 border border-white/[0.08]", isMobile ? "px-3 py-2" : "p-3 gap-4")}>
        {PhotoControl}
        <div className="flex-1 min-w-0">
          {titleEditOpen ? (
            <>
              <input
                type="text"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                maxLength={100}
                autoFocus={!isMobile}
                onFocus={(e) => e.currentTarget.select()}

                disabled={isSavingTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); handleSaveTitle(); }
                  else if (e.key === 'Escape') { e.preventDefault(); setTitleDraft(item.title || ''); setTitleEditOpen(false); }
                }}
                onBlur={() => {
                  // Save on blur if changed; otherwise just exit edit mode.
                  if (titleDraft.trim() === (item.title || '').trim()) {
                    setTitleEditOpen(false);
                  } else {
                    handleSaveTitle();
                  }
                }}
                className={cn(
                  "w-full bg-transparent border-0 outline-none p-0 font-semibold text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 leading-tight",
                  isMobile ? "text-base" : "text-lg"
                )}
                placeholder="Item title"
                aria-label="Edit title"
              />
              {metaLine ? (
                <p className={cn("text-muted-foreground mt-0.5", isMobile ? "text-[11px]" : "text-xs")}>
                  {metaLine}
                </p>
              ) : (
                <p className={cn("text-muted-foreground/70 mt-0.5", isMobile ? "text-[10px]" : "text-[10px]")}>
                  Enter to save · Esc to cancel
                </p>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => { setTitleDraft(item.title || ''); setTitleEditOpen(true); }}
                className="group/title text-left w-full inline-flex items-baseline gap-1.5 rounded-sm hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/20"
                aria-label="Edit title"
              >
                <span className={cn("font-semibold line-clamp-2 leading-tight", isMobile ? "text-base" : "text-lg")}>
                  {item.title}
                </span>
                <Pencil className="w-3.5 h-3.5 shrink-0 opacity-40 group-hover/title:opacity-90 transition-opacity translate-y-[1px]" />
              </button>
              {metaLine && (
                <p className={cn("text-muted-foreground mt-0.5", isMobile ? "text-[11px]" : "text-xs")}>
                  {metaLine}
                </p>
              )}
            </>
          )}
        </div>

      </div>
      <input ref={fileInputRef} type="file" accept="image/*,.heic,.heif,.dng" multiple onChange={handleFileSelect} className="hidden" />
      <input ref={cameraInputRef} type="file" accept="image/*,.heic,.heif,.dng" capture="environment" onChange={handleFileSelect} className="hidden" />
      
      {stockBlocked && (
        <div className={cn("flex items-start gap-2 rounded-lg border border-caution/25 bg-caution/10 leading-snug", isMobile ? "p-2.5 text-[11px]" : "p-3 text-[12px]")}>
          <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
          <div className="text-warning/80/90">
            Add a real photo to list this. {TIMESTAMP_TIP}
          </div>
        </div>
      )}
    </div>
  );

  // SECTION HEADER helper
  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className="space-y-3">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {children}
    </section>
  );

  // Cost field — labeled input with reveal toggle
  const CostField = (
    <div className="space-y-1.5">
      <Label htmlFor="quick-cost" className="text-sm">Your Cost{item.quantity > 1 ? ' (each)' : ''}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
        <Input
          id="quick-cost"
          type={showCost ? 'text' : 'password'}
          inputMode="decimal"
          placeholder={showCost ? '0.00' : '••••'}
          value={cost}
          onChange={e => setCost(e.target.value)}
          readOnly={!showCost}
          onFocus={() => { if (!showCost) setShowCost(true); }}
          className="pl-7 pr-24 h-11 field-surface field-border"
        />
        <button
          type="button"
          onClick={() => setShowCost(s => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-white/5"
        >
          {showCost ? <LockOpen className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
          {showCost ? 'Hide' : 'Reveal / Edit'}
        </button>
      </div>
      
    </div>
  );

  // Asking price field
  const collectrCents = resolveCollectrPriceCents(item);
  const currentPriceCents = Math.round((parseFloat(price || '0') || 0) * 100);
  const showCollectrHint = collectrCents !== null && currentPriceCents !== collectrCents;
  const PriceField = (
    <div className="space-y-1.5">
      <Label htmlFor="quick-price" className="text-sm">
        Asking Price<span className="text-muted-foreground font-normal"> (optional)</span>
      </Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
        <Input id="quick-price" type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" {...PRICE_INPUT_PROPS} placeholder="0.00" value={price} onChange={e => setPrice(e.target.value.replace(/[^0-9.]/g, ''))} onKeyDown={handleKeyDown} className="pl-7 h-11 field-surface field-border" autoFocus={!isMobile} />
      </div>
      {showCollectrHint && (
        <button
          type="button"
          onClick={() => setPrice((collectrCents! / 100).toFixed(2))}
          className="text-[11px] text-muted-foreground hover:text-active-gold transition-colors text-left"
        >
          Use Collectr import price: <span className="text-active-gold/90">{formatCents(collectrCents!)}</span>
        </button>
      )}
    </div>
  );

  const ebayStatusMeta = ebayListing ? (() => {
    const s = ebayListing.status;
    if (s === 'active') return { label: 'Listed on eBay', tone: 'text-active-gold', dot: 'bg-active-gold' };
    if (s === 'pending') return { label: 'Pending on eBay', tone: 'text-warning', dot: 'bg-warning' };
    if (s === 'ended') return { label: 'Ended on eBay', tone: 'text-muted-foreground', dot: 'bg-muted-foreground' };
    return { label: 'eBay error', tone: 'text-destructive', dot: 'bg-destructive' };
  })() : null;

  const ebayPriceLabel = ebayListing ? (() => {
    if (ebayListing.format === 'auction') {
      const start = (item as any).ebay_auction_start_cents as number | null | undefined;
      const bin = (item as any).ebay_auction_bin_cents as number | null | undefined;
      if (start == null && bin == null) return null;
      const parts: string[] = [];
      if (start != null) parts.push(`Start ${formatCents(start)}`);
      if (bin != null) parts.push(`BIN ${formatCents(bin)}`);
      return parts.join(' · ');
    }
    // Snapshot of what was actually pushed to eBay — NOT CardSimp's current asking price.
    return ebayListing.price_cents != null ? formatCents(ebayListing.price_cents) : null;
  })() : null;

  // Drift: CardSimp asking price differs from what's live on eBay, or legacy rows need a first price snapshot.
  const ebayPriceOutOfSync = !!ebayListing
    && ebayListing.status === 'active'
    && ebayListing.format !== 'auction'
    && item.asking_price_cents != null
    && (ebayListing.price_cents == null || ebayListing.price_cents !== item.asking_price_cents);

  const refreshAnchor = ebayListing?.last_refreshed_at ?? ebayListing?.listed_at;
  const refreshCooldown = refreshAnchor
    ? Math.max(0, 24 * 60 * 60 * 1000 - (Date.now() - new Date(refreshAnchor).getTime()))
    : 0;
  // Always allow clicking Refresh on active fixed-price listings. The server
  // enforces the 24h cooldown but bypasses it whenever there's a real price
  // change to push — so a save → refresh flow always works.
  const canRefreshEbay = !!ebayListing
    && ebayListing.status === 'active'
    && ebayListing.format !== 'auction';

  const refreshHint = ebayPriceOutOfSync
    ? 'Push new price to eBay — keeps watchers & history'
    : refreshCooldown > 0
      ? `Next free bump in ${Math.ceil(refreshCooldown / (60 * 60 * 1000))}h — save a new price to push now`
      : 'Bump this listing in eBay search — keeps watchers & history';

  const EbayStatusRow = ebayListing && ebayStatusMeta ? (
    <div className="flex items-start justify-between gap-3 pt-3 border-t border-white/[0.06]">
      <div className="flex items-start gap-3 min-w-0">
        <EbayBag className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
        <div className="space-y-0.5 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn("inline-block w-1.5 h-1.5 rounded-full", ebayStatusMeta.dot)} />
            <span className={cn("text-sm", ebayStatusMeta.tone)}>{ebayStatusMeta.label}</span>
            {ebayPriceLabel && (
              <span className="text-sm text-active-gold">· {ebayPriceLabel}</span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground truncate">
            {ebayListing.format === 'auction' ? 'Auction' : 'Fixed price'}
            {ebayListing.listing_id ? <> · ID {ebayListing.listing_id}</> : ' · Awaiting confirmation'}
            {ebayListing.listed_at && <> · {format(new Date(ebayListing.listed_at), 'MMM d')}</>}
            {ebayListing.last_refreshed_at && <> · Refreshed {format(new Date(ebayListing.last_refreshed_at), 'MMM d')}</>}
          </p>
          {ebayPriceOutOfSync && item.asking_price_cents != null && (
            <p className="text-[11px] text-warning/90">
              CardSimp price is {formatCents(item.asking_price_cents)} — Refresh to push to eBay.
            </p>
          )}
          {ebayListing.status === 'error' && ebayListing.last_error && (
            <p className="text-[11px] text-destructive/90 line-clamp-2">{ebayListing.last_error}</p>
          )}
        </div>
      </div>
      <div className="shrink-0 flex items-center gap-3">
        {ebayListing.status === 'active' && ebayListing.format !== 'auction' && (
          <button
            type="button"
            onClick={async () => {
              // If the user typed a new asking price but hasn't hit Save yet,
              // persist it first so Refresh actually pushes the new number.
              const typed = price.trim() ? parseFloat(price) : NaN;
              const typedCents = !isNaN(typed) && typed >= 0 ? Math.round(typed * 100) : null;
              const currentCents = item.asking_price_cents ?? null;
              if (typedCents !== currentCents && typedCents !== null) {
                try {
                  const { error } = await supabase
                    .from('user_items')
                    .update({ asking_price_cents: typedCents })
                    .eq('id', item.id);
                  if (error) throw error;
                  await queryClient.invalidateQueries({ queryKey: ['my-items'] });
                } catch (e) {
                  console.error('Failed to save price before refresh:', e);
                  toast.error('Could not save new price', { description: 'Try saving manually, then refresh.' });
                  return;
                }
              }
              refreshEbayListing.mutate();
            }}
            disabled={!canRefreshEbay || refreshEbayListing.isPending}
            className="text-[11px] text-active-gold hover:underline disabled:text-muted-foreground/60 disabled:no-underline disabled:cursor-not-allowed"
            title={refreshHint}
          >
            {refreshEbayListing.isPending ? 'Refreshing…' : 'Refresh'}
          </button>
        )}
        {ebayListing.listing_id && ebayListing.status === 'active' && (
          <a
            href={`https://www.ebay.com/itm/${ebayListing.listing_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-active-gold hover:underline"
          >
            View
          </a>
        )}
      </div>
    </div>
  ) : null;


  const VisibilityBlock = (
    <div className="space-y-1">
      <div className="flex items-center justify-between py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-3">
          <Tag className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm">List on CardSimp</span>
        </div>
        <Switch checked={isListed} onCheckedChange={setIsListed} />
      </div>
      <div className="py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageCircle className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm">Open to Offers</span>
          </div>
          <Switch checked={acceptsOffers} onCheckedChange={setAcceptsOffers} />
        </div>
        {!isMobile && acceptsOffers && !price.trim() && (
          <p className="mt-1.5 text-[11px] text-muted-foreground/70">Open to Offers is on — buyers will message you with offers, no asking price needed.</p>
        )}
      </div>
      {EbayStatusRow}
    </div>
  );



  // Main edit body — desktop = 2 columns w/ section headers, mobile = stacked & dense
  const MainBody = isMobile ? (
    <div className="space-y-4">
      <div className="space-y-3">
        {CostField}
        {PriceField}
      </div>
      {VisibilityBlock}
    </div>
  ) : (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {CostField}
        {PriceField}
      </div>
      <div className="grid grid-cols-2 gap-x-6 border-t border-white/[0.06] pt-1">
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <Tag className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm">List on CardSimp</span>
          </div>
          <Switch checked={isListed} onCheckedChange={setIsListed} />
        </div>
        <div className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageCircle className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm">Open to Offers</span>
            </div>
            <Switch checked={acceptsOffers} onCheckedChange={setAcceptsOffers} />
          </div>
          {acceptsOffers && !price.trim() && (
            <p className="mt-1.5 text-[11px] text-muted-foreground/70">Open to Offers is on — buyers will message you with offers, no asking price needed.</p>
          )}
        </div>
      </div>
      {EbayStatusRow}
    </div>
  );


  // Sold / Traded body — flat, edge-to-edge inside the dialog (no nested cards)
  const SoldTradeBody = (
    <div className="space-y-5">
      {!isMobile && (
        <button
          type="button"
          onClick={() => setShowSoldSection(false)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Back to edit
        </button>
      )}

      {/* Sold / Traded mode toggle — rounded-full segmented, brand active-gold active state */}
      <div
        className="grid grid-cols-2 gap-1 p-0.5 rounded-full bg-white/[0.05]"
        role="tablist"
        aria-label="Transaction type"
      >
        <button
          type="button"
          role="tab"
          aria-selected={transactionMode === 'sold'}
          onClick={() => setTransactionMode('sold')}
          className={cn(
            "h-9 rounded-full text-sm font-medium transition-colors flex items-center justify-center gap-2 border",
            transactionMode === 'sold'
              ? "bg-active-gold/10 text-active-gold border-active-gold/40"
              : "text-foreground/70 hover:text-foreground hover:bg-white/[0.04] border-transparent"
          )}
        >
          <span className="text-sm font-semibold leading-none">$</span>
          Sold
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={transactionMode === 'traded'}
          onClick={() => setTransactionMode('traded')}
          className={cn(
            "h-9 rounded-full text-sm font-medium transition-colors flex items-center justify-center gap-2 border",
            transactionMode === 'traded'
              ? "bg-active-gold/10 text-active-gold border-active-gold/40"
              : "text-foreground/70 hover:text-foreground hover:bg-white/[0.04] border-transparent"
          )}
        >
          <ArrowRightLeft className="w-3.5 h-3.5" />
          Traded
        </button>
      </div>

      {transactionMode === 'sold' && (
        <div className="space-y-4">
          {acceptedOffers.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="buyer-source">Sold to</Label>
              <Select value={buyerSource} onValueChange={setBuyerSource}>
                <SelectTrigger id="buyer-source" className="h-12 field-surface field-border">
                  <SelectValue placeholder="Select buyer" />
                </SelectTrigger>
                <SelectContent>
                  {acceptedOffers.map((offer) => (
                    <SelectItem key={offer.id} value={offer.id}>
                      {offer.sender?.username || 'CardSimp User'}
                      {offer.offer_cents ? ` — ${formatUsdCents(offer.offer_cents)}` : ''}
                    </SelectItem>
                  ))}
                  <SelectItem value="other">Someone else / External sale</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground/70">
                Linking the buyer lets you leave a review after the sale.
              </p>
            </div>
          )}

          {item.quantity > 1 && (
            <div className="space-y-2">
              <Label htmlFor="sold-quantity">Quantity Sold</Label>
              <div className="flex items-center gap-3">
                <Input id="sold-quantity" type="number" min={1} max={item.quantity} value={soldQuantity} onChange={e => setSoldQuantity(Math.min(Math.max(1, parseInt(e.target.value) || 1), item.quantity))} className="w-20 h-10 text-center" />
                <span className="text-sm text-muted-foreground">of {item.quantity} available</span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="sale-price">Sale Price {item.quantity > 1 && soldQuantity > 1 ? '(total)' : ''}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input id="sale-price" type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" {...PRICE_INPUT_PROPS} placeholder="0.00" value={salePrice} onChange={e => setSalePrice(e.target.value.replace(/[^0-9.]/g, ''))} className="pl-7 h-12" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Sale Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn("w-full h-12 justify-start text-left font-normal field-surface field-border", !saleDate && "text-muted-foreground")}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {saleDate ? format(saleDate, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={saleDate} onSelect={date => date && setSaleDate(date)} disabled={date => date > new Date()} initialFocus className={cn("p-3 pointer-events-auto")} />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>Payment</Label>
            <div className="flex gap-2">
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="flex-1 h-12 field-surface field-border text-sm">
                  <SelectValue placeholder="Payment method" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(method => (
                    <SelectItem key={method.value} value={method.value}>
                      <div className="flex items-center gap-2">
                        <method.icon className="w-3.5 h-3.5 text-muted-foreground" />
                        <span>{method.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <input ref={salePhotoInputRef} type="file" accept="image/*,.heic,.heif,.dng" capture="environment" className="hidden" onChange={handleSalePhotoUpload} />

              {salePhotoUrl ? (
                <div className="relative w-12 h-12 rounded-md overflow-hidden border border-white/[0.08] shrink-0">
                  <img src={salePhotoUrl} alt="Sale proof" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setSalePhotoUrl(null)} className="absolute -top-1 -right-1 w-4 h-4 bg-destructive rounded-full flex items-center justify-center hover:bg-destructive transition-colors">
                    <X className="w-2.5 h-2.5 text-white" />
                  </button>
                </div>
              ) : (
                <Button type="button" variant="outline" size="icon" aria-label="Upload sale photo" className="w-12 h-12 shrink-0 field-surface field-border" onClick={() => salePhotoInputRef.current?.click()} disabled={isUploadingSalePhoto}>
                  {isUploadingSalePhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4 text-muted-foreground" />}
                </Button>
              )}
            </div>
          </div>

          {profitLoss !== null && (
            <div className={cn("text-sm font-medium text-center py-2 rounded-md", profitLoss >= 0 ? "text-success bg-success/10" : "text-destructive bg-destructive/10")}>
              {profitLoss >= 0 ? '+' : ''}{formatUsd(profitLoss)} {profitLoss >= 0 ? 'profit' : 'loss'}
            </div>
          )}

          {!isMobile && (
            <Button onClick={handleMarkAsSold} className={cn("w-full h-12", goldGradientActive)} disabled={isSubmitting}>
              <CheckCircle className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Saving...' : 'Confirm Sale'}
            </Button>
          )}
        </div>
      )}

      {transactionMode === 'traded' && (
        <div className="space-y-5">
          {/* Trading Away — flat row, no nested card */}
          <section className="space-y-3">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Trading Away</Label>

            <div className="flex items-center gap-3">
              {item.images?.[0]?.image_url || item.stock_image_url ? (
                <img src={item.images?.[0]?.image_url || item.stock_image_url || ''} alt={item.title} className="w-10 h-10 rounded object-cover shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded bg-white/[0.05] flex items-center justify-center shrink-0">
                  <span className="text-xs text-muted-foreground">N/A</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{item.title}</p>
                {item.cost_basis_cents && (
                  <span className="text-xs text-muted-foreground">Cost: {formatUsdCents(item.cost_basis_cents)}</span>
                )}
              </div>
            </div>

            {gaveItems.length > 0 && (
              <div className="space-y-2">
                {gaveItems.map(gi => (
                  <TradeItemCard key={gi.id} item={gi} variant="gave" onRemove={() => setGaveItems(prev => prev.filter(i => i.id !== gi.id))} />
                ))}
              </div>
            )}

            <InventorySearchPopover
              open={showGaveSearch}
              onOpenChange={setShowGaveSearch}
              multiSelect
              onSelectMany={handleAddManyGaveFromInventory}
              excludeIds={getExcludedIds()}
              trigger={
                <button type="button" className="w-full h-10 px-3 rounded-md field-surface field-border hover:border-white/20 inline-flex items-center justify-center gap-1.5 text-xs text-foreground transition-colors">
                  <Plus className="w-3.5 h-3.5 text-muted-foreground" />
                  Add More from Inventory
                </button>
              }
            />

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">+ Cash Paid</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" {...PRICE_INPUT_PROPS} placeholder="0.00" value={cashPaid} onChange={e => setCashPaid(e.target.value.replace(/[^0-9.]/g, ''))} className="pl-8 h-12 field-surface field-border" />
              </div>
            </div>
          </section>

          {/* Divider between halves */}
          <div className="border-t border-white/[0.06]" />

          {/* Received — flat */}
          <section className="space-y-3">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Received</Label>
            {receivedItems.length > 0 && (
              <div className="space-y-2">
                {receivedItems.map(ri => (
                  <TradeItemCard key={ri.id} item={ri} variant="received" onRemove={() => setReceivedItems(prev => prev.filter(i => i.id !== ri.id))} />
                ))}
              </div>
            )}
            {/* Inline composer: title + optional value. Blank title → "Trade-in (needs details)" placeholder. */}
            <div className="flex gap-2 items-stretch">
              <Input
                value={newReceivedTitle}
                onChange={e => setNewReceivedTitle(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddReceivedInline();
                  }
                }}
                placeholder="Item title"
                className="flex-1 h-10 field-surface field-border text-sm"
              />
              <div className="relative w-24">
                <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*\.?[0-9]*"
                  {...PRICE_INPUT_PROPS}
                  value={newReceivedValue}
                  onChange={e => setNewReceivedValue(e.target.value.replace(/[^0-9.]/g, ''))}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddReceivedInline();
                    }
                  }}
                  placeholder="Value"
                  className="pl-7 h-10 field-surface field-border text-sm"
                />
              </div>
              <button
                type="button"
                onClick={handleAddReceivedInline}
                aria-label="Add received item"
                className="h-10 w-10 rounded-md field-surface field-border hover:border-white/20 flex items-center justify-center shrink-0 transition-colors"
              >
                <Plus className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            {receivedItems.some(r => r.title.startsWith('Trade-in (needs details)')) && (
              <p className="text-[11px] text-muted-foreground leading-snug">
                Placeholders save as private drafts in your inventory — finish details (title, photos, condition) later from Inventory.
              </p>
            )}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">+ Cash Received</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" {...PRICE_INPUT_PROPS} placeholder="0.00" value={cashReceived} onChange={e => setCashReceived(e.target.value.replace(/[^0-9.]/g, ''))} className="pl-8 h-12 field-surface field-border" />
              </div>
            </div>
          </section>

          <div className="space-y-2">
            <Label>Trade Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn("w-full h-12 justify-start text-left font-normal field-surface field-border", !saleDate && "text-muted-foreground")}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {saleDate ? format(saleDate, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={saleDate} onSelect={date => date && setSaleDate(date)} disabled={date => date > new Date()} initialFocus className={cn("p-3 pointer-events-auto")} />
              </PopoverContent>
            </Popover>
          </div>

          <TradeSummary
            receivedItems={receivedItems}
            gaveItems={[
              { id: item.id, title: item.title, estimatedValueCents: item.cost_basis_cents, isFromInventory: true, inventoryItemId: item.id },
              ...gaveItems,
            ]}
            cashReceivedCents={cashReceived ? Math.round(parseFloat(cashReceived) * 100) : 0}
            cashPaidCents={cashPaid ? Math.round(parseFloat(cashPaid) * 100) : 0}
          />

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Trade Photo (Optional)</Label>
            <input ref={tradePhotoInputRef} type="file" accept="image/*,.heic,.heif,.dng" capture="environment" className="hidden" onChange={handleTradePhotoUpload} />
            {tradePhotoUrl ? (
              <div className="relative w-full h-32 rounded-md overflow-hidden border border-white/[0.08]">
                <img src={tradePhotoUrl} alt="Trade proof" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setTradePhotoUrl(null)} className="absolute top-1 right-1 w-6 h-6 bg-destructive rounded-full flex items-center justify-center hover:bg-destructive transition-colors">
                  <X className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
            ) : (
              <Button type="button" variant="outline" className="w-full h-12 field-surface field-border" onClick={() => tradePhotoInputRef.current?.click()} disabled={isUploadingTradePhoto}>
                {isUploadingTradePhoto ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2 text-muted-foreground" />}
                <span className="text-sm">{isUploadingTradePhoto ? 'Uploading...' : 'Add Photo'}</span>
              </Button>
            )}
          </div>

          {!isMobile && (
            <Button onClick={handleRecordTrade} className={cn("w-full h-12", goldGradientActive)} disabled={isSubmitting}>
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Processing...' : 'Confirm Trade'}
            </Button>
          )}
        </div>
      )}

      
    </div>
  );


  // More-actions menu (footer) — bottom sheet on mobile, dropdown on desktop
  const handleEbayClick = () => {
    // Resolve effective price: typed value (unsaved) takes precedence.
    // No asking price is fine — CardSimp lists it as "Taking Offers" and the
    // eBay dialog lets the seller set the eBay price directly.
    const typed = price.trim() ? parseFloat(price) : null;
    const typedValid = typed !== null && !isNaN(typed) && typed > 0;

    // Auto-persist any unsaved price/cost edits before opening eBay flow.
    // Also silently promote the item to `listed` on CardSimp if it isn't
    // already — the two storefronts stay in sync, but the user only sees
    // the eBay flow open.
    const typedCents = typedValid ? Math.round((typed as number) * 100) : item.asking_price_cents ?? null;
    const costTyped = cost.trim() ? parseFloat(cost) : null;
    const costCents = costTyped !== null && !isNaN(costTyped) && costTyped >= 0
      ? Math.round(costTyped * 100)
      : item.cost_basis_cents ?? null;
    const priceDirty = typedCents !== (item.asking_price_cents ?? null);
    const costDirty = costCents !== (item.cost_basis_cents ?? null);
    const needsList = item.visibility !== 'listed';

    const openDialog = () => setEbayDialogOpen(true);

    if (priceDirty || costDirty || needsList) {
      (async () => {
        try {
          const patch: {
            asking_price_cents: number | null;
            cost_basis_cents: number | null;
            visibility?: 'listed';
          } = {
            asking_price_cents: typedCents,
            cost_basis_cents: costCents,
          };
          if (needsList) patch.visibility = 'listed';
          const { error } = await supabase
            .from('user_items')
            .update(patch)
            .eq('id', item.id);
          if (error) throw error;
          await queryClient.invalidateQueries({ queryKey: ['my-items'] });
          openDialog();
        } catch (err) {
          console.error('Auto-save before eBay failed:', err);
          toast.error('Could not save your price. Please tap Save and try again.');
        }
      })();
    } else {
      openDialog();
    }
  };

  const moreMenuItems: ActionMenuItem[] = [
    ...(!isOnItemPage ? [{
      key: 'view',
      label: 'View item page',
      icon: <ExternalLink className="w-5 h-5" />,
      onSelect: () => {
        const slug = (item as { short_id?: string | null }).short_id || item.id;
        handleClose();
        navigate(`/item/${slug}`);
      },
    } as ActionMenuItem] : []),
    {
      key: 'edit',
      label: 'Edit all details',
      icon: <Settings2 className="w-5 h-5" />,
      onSelect: () => setEditModalOpen(true),
    },

    {
      key: 'duplicate',
      label: 'Duplicate item',
      icon: <Copy className="w-5 h-5" />,
      disabled: duplicateItem.isPending,
      onSelect: () => {
        if (duplicateItem.isPending) return;
        duplicateItem.mutate(item.id, {
          onSuccess: (newItem: any) => {
            toast.success('Duplicated — set condition & price');
            handleClose();
            if (newItem?.id) {
              useUIStore.getState().requestEditItem(newItem.id);
            }
          },
          onError: (err: any) => toast.error(err?.message || 'Failed to duplicate item'),
        });
      },
    },
    ...((item.quantity ?? 1) > 1 ? [{
      key: 'split',
      label: `Split into ${item.quantity} copies`,
      icon: <Scissors className="w-5 h-5" />,
      onSelect: () => setShowSplitConfirm(true),
    } as ActionMenuItem] : []),
    {
      key: 'sold',
      label: 'Mark as Sold or Traded',
      icon: <DollarSign className="w-5 h-5 text-success" />,
      onSelect: () => { setTransactionMode('sold'); setShowSoldSection(true); },
    },
    ...(!item.sold_at && ebayConnection?.is_active ? [(() => {
      const isLive = ebayListing?.status === 'active' && ebayListing.listing_id;
      return {
        key: 'ebay',
        label: isLive ? 'Manage eBay Listing' : 'List on eBay',
        icon: <EbayBag className="w-5 h-5" />,
        onSelect: handleEbayClick,
      } as ActionMenuItem;
    })()] : []),
    {
      key: 'delete',
      label: 'Delete this item',
      icon: <Trash2 className="w-5 h-5" />,
      destructive: true,
      separatorBefore: true,
      onSelect: () => setShowDeleteConfirm(true),
    },
  ];

  const MoreMenu = (
    <ActionMenu
      align="end"
      mobileTitle="Item actions"
      trigger={
        <Button
          variant="outline"
          aria-label="More Options"
          className={cn(
            "bg-white/[0.05] border-white/[0.08] gap-2",
            isMobile ? "h-11 w-full" : "h-11 flex-1"
          )}
          disabled={isSubmitting}
        >
          <Settings2 className="w-4 h-4" />
          More Options
        </Button>
      }
      items={moreMenuItems}
    />
  );

  // Footer — sticky on both layouts
  const Footer = showSoldSection ? null : (
    <div className={cn("border-t border-white/[0.06]", isMobile ? "mt-3 pt-3 flex flex-col-reverse gap-2" : "mt-5 pt-4 flex items-center gap-2")}>
      {MoreMenu}
      <Button className={cn(isMobile ? "h-12 w-full text-base font-semibold" : "h-11 flex-1")} onClick={handleSave} disabled={isSubmitting}>
        {isSubmitting ? 'Saving...' : 'Save'}
      </Button>
    </div>
  );

 

  // Mobile fullscreen footer for Sold/Trade — sticky, keyboard-aware via MobileFullscreenPage
  const SoldTradeFooter = transactionMode === 'sold' ? (
    <Button onClick={handleMarkAsSold} className={cn("w-full h-12", goldGradientActive)} disabled={isSubmitting}>
      <CheckCircle className="w-4 h-4 mr-2" />
      {isSubmitting ? 'Saving...' : 'Confirm Sale'}
    </Button>
  ) : (
    <Button onClick={handleRecordTrade} className={cn("w-full h-12", goldGradientActive)} disabled={isSubmitting}>
      <ArrowRightLeft className="w-4 h-4 mr-2" />
      {isSubmitting ? 'Processing...' : 'Confirm Trade'}
    </Button>
  );

  // Mobile body for the default Manage Item view (footer moved to MobileFullscreenPage footer slot).
  const MobileBodyContent = (
    <div className="px-4 pt-4 pb-6 space-y-4">
      {HeaderBlock}
      {MainBody}
    </div>
  );

  // Mobile footer for the default Manage Item view — sticky, keyboard-aware via MobileFullscreenPage.
  const MainFooter = showSoldSection ? null : (
    <div className="flex flex-col-reverse gap-2">
      {MoreMenu}
      <Button className="h-12 w-full text-base font-semibold" onClick={handleSave} disabled={isSubmitting}>
        {isSubmitting ? 'Saving...' : 'Save'}
      </Button>
    </div>
  );

  return <>
      {isMobile && showSoldSection ? (
        // Sold / Trade is a fullscreen glass page on mobile — back chevron returns to Manage Item.
        <MobileFullscreenPage
          open={open}
          onClose={() => setShowSoldSection(false)}
          title={transactionMode === 'sold' ? 'Mark as Sold' : 'Record Trade'}
          surface="glass"
          footer={SoldTradeFooter}
        >
          <div className="px-4 pt-4 pb-6 space-y-5">
            {HeaderBlock}
            {SoldTradeBody}
          </div>
        </MobileFullscreenPage>
      ) : isMobile ? (
        // Manage Item root is a fullscreen glass page on mobile — back chevron closes the dialog.
        <MobileFullscreenPage
          open={open}
          onClose={handleClose}
          title="Manage Item"
          surface="glass"
          footer={MainFooter}
        >
          {MobileBodyContent}
        </MobileFullscreenPage>
      ) : (
        // Desktop: single-scroll glass dialog. Header/body/footer scroll together.
        // See mem://style/desktop-dialog-single-scroll-v1.
        <Dialog open={open} onOpenChange={isOpen => !isOpen && handleClose()}>
          <DialogContent className="sm:max-w-2xl w-full max-h-[85dvh] overflow-y-auto p-0">
            <div className="px-6 pt-6 pb-6 space-y-6">
              <DialogHeader>
                <DialogTitle>Manage Item</DialogTitle>
              </DialogHeader>
              {HeaderBlock}
              {showSoldSection ? SoldTradeBody : MainBody}
              {Footer}
            </div>
          </DialogContent>
        </Dialog>
      )}


      <ItemFormModal item={item} open={editModalOpen} onOpenChange={isOpen => {
      setEditModalOpen(isOpen);
      if (!isOpen) {
        onClose();
      }
    }} />

      <ImageSelectionDialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} item={item} hasExistingImages={!!item.images?.[0]?.image_url} stockOnly />

      {/* Post-sale review prompt — shown when the sale was linked to a known buyer.
          Mirrors the + menu's MarkAsSoldDialog flow so both surfaces feel identical. */}
      {pendingReview && (
        <SaleReviewPrompt
          open={!!pendingReview}
          transactionId={pendingReview.transactionId}
          buyerId={pendingReview.buyerId}
          buyerName={pendingReview.buyerName}
          onDismiss={() => {
            setPendingReview(null);
            handleClose();
          }}
        />
      )}

      {/* Manage photos — bottom sheet on mobile, glass dialog on desktop. Same body, same components. */}
      <ManagePhotosDialog
        open={reorderOpen}
        onOpenChange={(o) => { if (!o) setProofError(null); setReorderOpen(o); }}
        isMobile={isMobile}
        proofError={proofError}
        dndSensors={dndSensors}
        reorderDraft={reorderDraft}
        setReorderDraft={setReorderDraft}
        existingProof={existingProof}
        verifyingProofUrl={verifyingProofUrl}
        isVerifyingProof={isVerifyingProof}
        isSavingProof={isSavingProof}
        proofEligible={proofEligible}
        isSavingReorder={isSavingReorder}
        onClearProof={() => handleSetProofUrl(null)}
        onVerifyProof={verifyAndSetProof}
        onRequestDelete={setPendingDeleteUrl}
        onCancel={() => setReorderOpen(false)}
        onSave={saveReorder}
        fileInputRef={fileInputRef}
        proofCameraInputRef={proofCameraInputRef}
        proofGalleryInputRef={proofGalleryInputRef}
        handleProofPhotoUpload={handleProofPhotoUpload}
      />

      <DeletePhotoConfirmDialog
        pendingDeleteUrl={pendingDeleteUrl}
        isDeletingPhoto={isDeletingPhoto}
        onCancel={() => setPendingDeleteUrl(null)}
        onConfirm={confirmDeletePhoto}
      />


      <AlertDialog
        open={showSplitConfirm}
        onOpenChange={(open) => {
          setShowSplitConfirm(open);
          if (!open) setSplitImageTargets({});
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Scissors className="w-5 h-5 text-active-gold" />
              Split into {item.quantity} items
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-white/80">
              Each copy becomes its own listing — photos, condition, and price set per copy.
              Cost basis splits per unit, asking price clears. Proof-of-possession stays with copy 1;
              the others need their own before listing.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {(() => {
            const proofUrl = (item.proof_images as Record<string, string> | null)?.possession ?? null;
            const allImages = item.images ?? [];
            const galleryImages = allImages.filter((img) => img.image_url !== proofUrl);
            if (galleryImages.length === 0) return null;

            const copyCount = item.quantity ?? 1;
            return (
              <div className="mt-1 space-y-2">
                <div className="text-xs font-medium text-white/70">Assign photos</div>
                <div className="max-h-[40dvh] overflow-y-auto rounded-lg border border-white/10 divide-y divide-white/5">
                  {galleryImages.map((img) => {
                    const current = splitImageTargets[img.id] ?? 0;
                    return (
                      <div key={img.id} className="flex items-center gap-3 p-2">
                        <img
                          src={img.image_url}
                          alt=""
                          className="w-12 h-12 rounded-md object-cover bg-black/30 shrink-0"
                        />
                        <div className="flex-1 flex flex-wrap gap-1">
                          {Array.from({ length: copyCount }).map((_, idx) => {
                            const active = current === idx;
                            return (
                              <button
                                key={idx}
                                type="button"
                                onClick={() =>
                                  setSplitImageTargets((prev) => ({ ...prev, [img.id]: idx }))
                                }
                                className={cn(
                                  'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors',
                                  active
                                    ? 'bg-active-gold/10 text-active-gold border-active-gold/40'
                                    : 'bg-white/[0.04] text-white/70 border-white/10 hover:bg-white/[0.08]',
                                )}
                              >
                                Copy {idx + 1}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {proofUrl && (
                  <p className="text-[11px] text-white/50">
                    Proof photo stays with Copy 1.
                  </p>
                )}
              </div>
            );
          })()}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSplitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSplitAndList} disabled={isSplitting}>
              <Scissors className="w-4 h-4 mr-2" />
              {isSplitting ? 'Splitting…' : `Split into ${item.quantity} items`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <DeleteItemsConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        count={1}
        itemTitle={item.title}
        isDeleting={isDeleting}
        onConfirm={handleDeleteItem}
      />
      <EbayListingDialog
        itemId={item.id}
        hasPrice={!!item.asking_price_cents && item.asking_price_cents > 0}
        open={ebayDialogOpen}
        onOpenChange={setEbayDialogOpen}
      />
    </>;
}
